import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { CheckCircle, AlertCircle, Loader2 } from "lucide-react";
import { supabase } from "@/lib/supabase";
import { useAuth } from "@/hooks/use-auth";
import { useQueryClient } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";

// Plain validator, not zod: route search validators stay in the eagerly
// loaded route tree, so a zod schema here would put zod in the entry chunk.
function validateInviteSearch(search: Record<string, unknown>): {
  token: string;
} {
  return { token: typeof search.token === "string" ? search.token : "" };
}

type InviteDetails = {
  invitation_id: string;
  email: string;
  inviter_email: string | null;
  role: string;
  status: string;
  expires_at: string;
};

const RPC_ERROR_KEYS: Record<string, string> = {
  invalid_or_used_token: "invitePage.invalidOrUsed",
  token_expired: "invitePage.expired",
  not_authenticated: "invitePage.notAuthenticated",
  wrong_user: "invitePage.wrongUser",
  last_admin: "users.errors.lastAdmin",
};

function InvitePage() {
  const { t } = useTranslation();
  const { token } = Route.useSearch();
  const { user, signOut } = useAuth();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const [invite, setInvite] = useState<InviteDetails | null>(null);
  const [loadError, setLoadError] = useState<string | null>(() =>
    !token ? t("invitePage.invalidLink") : null,
  );
  const [busy, setBusy] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);
  const [accepted, setAccepted] = useState(false);

  useEffect(() => {
    if (!token) return;

    supabase
      .rpc("get_invitation_details", { p_token: token })
      .then(({ data, error }) => {
        if (error || !data || data.length === 0) {
          setLoadError(t("invitePage.invalidOrExpired"));
          return;
        }
        const details = data[0] as InviteDetails;
        if (details.status === "accepted") {
          setLoadError(t("invitePage.alreadyAccepted"));
          return;
        }
        if (
          details.status === "expired" ||
          new Date(details.expires_at) < new Date()
        ) {
          setLoadError(t("invitePage.expired"));
          return;
        }
        setInvite(details);
      });
  }, [token, t]);

  const emailMatches =
    user?.email &&
    invite?.email &&
    user.email.toLowerCase() === invite.email.toLowerCase();

  const finalize = async () => {
    const { data: acceptData, error: acceptError } = await supabase.rpc(
      "accept_workspace_invitation",
      { p_token: token },
    );
    if (acceptError) {
      setActionError(acceptError.message || t("invitePage.acceptFailed"));
      return false;
    }
    const result = acceptData as { error?: string; success?: boolean };
    if (result?.error) {
      const key = RPC_ERROR_KEYS[result.error];
      setActionError(key ? t(key) : t("invitePage.acceptFailed"));
      return false;
    }

    // Fire-and-forget team-welcome email — don't block navigation on it.
    if (user?.id) {
      void supabase.functions.invoke("send-emails", {
        body: { emailTypes: ["team_welcome"], userId: user.id },
      });
    }

    queryClient.invalidateQueries();
    setAccepted(true);
    setTimeout(() => navigate({ to: "/generate" }), 1500);
    return true;
  };

  const acceptAsExistingUser = async () => {
    setBusy(true);
    setActionError(null);
    await finalize();
    setBusy(false);
  };

  const acceptAsNewOrSignIn = async () => {
    setBusy(true);
    setActionError(null);

    const { data, error } = await supabase.functions.invoke(
      "prepare-invite-acceptance",
      { body: { token } },
    );

    if (error || !data?.hashed_token) {
      setActionError(t("invitePage.acceptFailed"));
      setBusy(false);
      return;
    }

    const { error: verifyError } = await supabase.auth.verifyOtp({
      token_hash: data.hashed_token,
      type: data.type === "invite" ? "invite" : "magiclink",
    });

    if (verifyError) {
      setActionError(verifyError.message || t("invitePage.acceptFailed"));
      setBusy(false);
      return;
    }

    // Re-fetch session-bound user data, then accept.
    await finalize();
    setBusy(false);
  };

  const signOutAndAccept = async () => {
    setBusy(true);
    setActionError(null);
    await signOut();
    // After signOut, user becomes null; calling acceptAsNewOrSignIn re-enters
    // the magic-link flow under the invitee's email.
    await acceptAsNewOrSignIn();
  };

  return (
    <div className="bg-background flex min-h-screen items-center justify-center p-4">
      <div className="bg-card w-full max-w-md rounded-xl border p-8 shadow-sm">
        <h1 className="text-foreground mb-2 text-2xl font-semibold">
          CarBackground.ai
        </h1>

        {!invite && !loadError && (
          <div className="text-muted-foreground flex items-center gap-2">
            <Loader2 className="animate-spin" size={18} />
            <span>{t("invitePage.loadingInvite")}</span>
          </div>
        )}

        {loadError && (
          <div className="space-y-4">
            <div className="text-destructive flex items-start gap-3">
              <AlertCircle size={20} className="mt-0.5 shrink-0" />
              <p>{loadError}</p>
            </div>
            <Button
              variant="outline"
              onClick={() => navigate({ to: "/sign-in" })}
            >
              {t("invitePage.goToSignIn")}
            </Button>
          </div>
        )}

        {accepted && (
          <div className="space-y-4">
            <div className="flex items-start gap-3 text-teal-600">
              <CheckCircle size={20} className="mt-0.5 shrink-0" />
              <div>
                <p className="font-medium">{t("invitePage.welcome")}</p>
                <p className="text-muted-foreground text-sm">
                  {t("invitePage.redirecting")}
                </p>
              </div>
            </div>
          </div>
        )}

        {invite && !accepted && (
          <div className="space-y-6">
            <div>
              <p className="text-xl font-semibold">{t("invitePage.title")}</p>
              <p className="text-muted-foreground mt-2 text-sm">
                {(() => {
                  const isAdmin = invite.role === "admin";
                  if (invite.inviter_email) {
                    return t(
                      isAdmin
                        ? "invitePage.invitedByAsAdmin"
                        : "invitePage.invitedByAsMember",
                      { email: invite.inviter_email },
                    );
                  }
                  return t(
                    isAdmin
                      ? "invitePage.invitedAsAdmin"
                      : "invitePage.invitedAsMember",
                  );
                })()}
              </p>
            </div>

            {/* State C: signed in as the wrong account */}
            {user && !emailMatches && (
              <div className="bg-muted/50 rounded-md border p-3 text-sm">
                <p className="text-foreground">
                  {t("invitePage.signedInWrongAccount", {
                    current: user.email ?? "",
                    invited: invite.email,
                  })}
                </p>
              </div>
            )}

            {actionError && (
              <div className="text-destructive flex items-start gap-3 text-sm">
                <AlertCircle size={16} className="mt-0.5 shrink-0" />
                <p>{actionError}</p>
              </div>
            )}

            {user && emailMatches ? (
              // State B
              <Button
                className="w-full"
                onClick={acceptAsExistingUser}
                disabled={busy}
              >
                {busy && <Loader2 className="mr-2 animate-spin" size={16} />}
                {t("invitePage.accept")}
              </Button>
            ) : user && !emailMatches ? (
              // State C
              <Button
                className="w-full"
                onClick={signOutAndAccept}
                disabled={busy}
              >
                {busy && <Loader2 className="mr-2 animate-spin" size={16} />}
                {t("invitePage.signOutAndAccept", { email: invite.email })}
              </Button>
            ) : (
              // State A
              <Button
                className="w-full"
                onClick={acceptAsNewOrSignIn}
                disabled={busy}
              >
                {busy && <Loader2 className="mr-2 animate-spin" size={16} />}
                {t("invitePage.accept")}
              </Button>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

export const Route = createFileRoute("/invite")({
  validateSearch: validateInviteSearch,
  component: InvitePage,
});
